import {
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AutocompleteArrayInput,
    DateInput,
    RadioButtonGroupInput,
    ReferenceArrayInput,
    SelectArrayInput,
    SelectInput,
    TextInput,
    email,
    required,
    useRecordContext,
} from 'react-admin';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { MemberInput } from '@domain/member';
import ImageEditorField from '@app/misc/ImageEditorField';
import { Label } from '@domain/labels';
import { useFormContext } from 'react-hook-form';
import {
    antennaOptionText,
    antennaInputText,
} from '@app/antenna/AntennaInputOption';
import { AddressAutofillInputs } from '@app/misc/AddressAutofillInputs';
import { useResource } from '@app/misc/useResource';

export const MemberInputs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Stack gap={2} p={1}>
            <MemberNameInputs />
            <Stack gap={3} direction={isMobile ? 'column' : 'row'}>
                <Stack gap={4} flex={5}>
                    <MemberPersonalInformationInputs />
                    <MemberAddressInputs />
                </Stack>
                <Divider
                    orientation={isMobile ? 'horizontal' : 'vertical'}
                    flexItem
                />
                <Stack gap={4} flex={4}>
                    <MemberPositionInputs />
                </Stack>
            </Stack>
        </Stack>
    );
};

const MemberNameInputs = () => {
    const { getValues } = useFormContext();
    const record =
        useRecordContext<MemberInput>() ?? (getValues() as MemberInput);
    const { data: avatarSrc } = useResource(record?.avatarId);

    return (
        <Stack gap={2} flex={1} direction="row">
            <ImageEditorField
                source="avatarInput"
                type="avatar"
                emptyText={`${record.firstName?.charAt(0).toUpperCase() ?? ''}${record.lastName?.charAt(0).toUpperCase() ?? ''}`}
                linkPosition="bottom"
                src={avatarSrc ?? undefined}
            />

            <TextInput
                source="firstName"
                validate={required()}
                helperText={false}
            />
            <TextInput
                source="lastName"
                validate={required()}
                helperText={false}
            />
        </Stack>
    );
};

const MemberPersonalInformationInputs = () => {
    const { memberGenderLabels: memberGender } = useConfigurationContext();
    return (
        <Stack>
            <Typography variant="h6">Personal information</Typography>
            <RadioButtonGroupInput
                label={false}
                source="gender"
                choices={memberGender}
                helperText={false}
                optionText="label"
                optionValue="value"
                sx={{ '& .MuiRadio-root': { paddingY: 0 } }}
                defaultValue={memberGender[0].value}
            />
            <DateInput source="birthDate" helperText={false} />

            <TextInput
                source="email"
                label="Email"
                helperText={false}
                validate={email()}
            />

            <TextInput
                source="emailOrga"
                label="WWF email"
                helperText={false}
                validate={email()}
            />

            <TextInput
                source="phoneNumber"
                label="Phone number"
                helperText={false}
            />

            <TextInput
                source="job"
                label="Professional job"
                helperText={false}
            />
        </Stack>
    );
};

const MemberPositionInputs = () => {
    const { memberTypeLabels, volunteerMissionAffinityLabels } =
        useConfigurationContext();

    return (
        <Stack>
            <Typography variant="h6">Position</Typography>
            <SelectInput
                source="type"
                choices={memberTypeLabels}
                optionValue="value"
                optionText={labelRenderer}
                helperText={false}
                isRequired={true}
            />

            <ReferenceArrayInput
                source="antennaIds"
                reference="antenna"
                sort={{ field: 'name', order: 'ASC' }}
            >
                <AutocompleteArrayInput
                    label="Antennas"
                    optionText={antennaOptionText}
                    inputText={antennaInputText}
                    helperText={false}
                    filterToQuery={search => ({ search })}
                />
            </ReferenceArrayInput>
            <DateInput source="sinceDate" helperText={false} />

            <Typography variant="h6">Volunteer details</Typography>
            <SelectArrayInput
                source="missionAffinity"
                choices={volunteerMissionAffinityLabels}
                optionValue="value"
                optionText={labelRenderer}
                helperText={false}
            />
        </Stack>
    );
};

const MemberAddressInputs = () => {
    return (
        <Stack>
            <Typography variant="h6">Address</Typography>
            <AddressAutofillInputs />
        </Stack>
    );
};

const labelRenderer = (choice: Label<any>) => <>{choice.label}</>;
